import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toastError, toastSuccess } from '@/lib/toast';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Titulo from '@/components/tipografia/Titulo';
import Descricao from '@/components/tipografia/Descricao';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Badge from '@/components/layout/Badge';
import Modal from '@/components/layout/Modal';
import Icon from '@mdi/react';
import {
    mdiLoading,
    mdiPencil,
    mdiPlus,
    mdiTrashCan,
    mdiToggleSwitch,
    mdiToggleSwitchOffOutline,
    mdiSwapHorizontal,
} from '@mdi/js';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoNumero from '@/components/Formulario/CampoNumero';
import { extractApiError } from '@/lib/apiError';
import type { ApplianceDTO, NetworkLanDeviceDTO } from '@webip/shared/src/dto/appliance';
import { useAppliance } from '@/lib/useAppliance';
import type { PortForwardRuleDTO, PortForwardProtocol } from '@webip/shared/src/dto/nat';
import type { InboundPolicyDTO } from '@webip/shared/src/dto/firewall_entrada';

// ─── DADOS ESTÁTICOS ──────────────────────────────────────────────────────────

const PROTOCOL_OPTIONS = [
    { id: 'tcp', label: 'TCP' },
    { id: 'udp', label: 'UDP' },
    { id: 'both', label: 'TCP + UDP' },
];

const PROTOCOL_LABEL: Record<PortForwardProtocol, string> = {
    tcp: 'TCP',
    udp: 'UDP',
    both: 'TCP+UDP',
};

// ─── TIPOS INTERNOS ────────────────────────────────────────────────────────────

type FormState = {
    name: string;
    enabled: boolean;
    protocol: PortForwardProtocol | '';
    externalPort: string;
    internalDeviceId: string;
    internalIp: string;
    internalPort: string;
    wanId: string;
    comment: string;
};

const EMPTY_FORM: FormState = {
    name: '',
    enabled: true,
    protocol: 'tcp',
    externalPort: '',
    internalDeviceId: '',
    internalIp: '',
    internalPort: '',
    wanId: '',
    comment: '',
};

// ─── VALIDAÇÃO LOCAL ──────────────────────────────────────────────────────────

function isValidPort(v: string): boolean {
    if (!/^\d+$/.test(v)) return false;
    const n = Number(v);
    return n >= 1 && n <= 65535;
}

function isValidExternalPort(v: string): boolean {
    v = v.trim();
    if (/^\d+$/.test(v)) return isValidPort(v);
    const m = v.match(/^(\d+)-(\d+)$/);
    if (!m) return false;
    return isValidPort(m[1]) && isValidPort(m[2]) && Number(m[1]) <= Number(m[2]);
}

function portRangeCount(v: string): number {
    const m = v.trim().match(/^(\d+)-(\d+)$/);
    if (!m) return 1;
    return Number(m[2]) - Number(m[1]) + 1;
}

/** Porta interna é opcional. Se informada, deve ser porta/range válido com a mesma contagem de portas que a externa. */
function isValidInternalPort(v: string, externalPort: string): boolean {
    if (!v.trim()) return true;
    if (!isValidExternalPort(v)) return false;
    return portRangeCount(v) === portRangeCount(externalPort);
}

function isValidIPv4(v: string): boolean {
    const chunks = v.split('.');
    if (chunks.length !== 4) return false;
    return chunks.every((c) => /^\d+$/.test(c) && Number(c) >= 0 && Number(c) <= 255);
}

function validateForm(form: FormState): string | null {
    if (!form.name.trim()) return 'Nome é obrigatório';
    if (!form.protocol) return 'Protocolo é obrigatório';
    if (!isValidExternalPort(form.externalPort)) return "Porta externa inválida (ex: '80' ou '8080-8090')";
    if (!form.internalIp.trim() || !isValidIPv4(form.internalIp.trim())) return 'IP interno deve ser um IPv4 válido';
    if (form.internalPort.trim() && !isValidInternalPort(form.internalPort, form.externalPort)) {
        const count = portRangeCount(form.externalPort);
        return count > 1
            ? `Porta interna inválida — informe um range de ${count} portas (igual à externa)`
            : 'Porta interna inválida (1-65535)';
    }
    return null;
}

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────────

export default function NatPage() {
    const qc = useQueryClient();
    const { applianceId: selectedApplianceId, selectedAppliance } = useAppliance();

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<PortForwardRuleDTO | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formError, setFormError] = useState<string | null>(null);
    const [togglingId, setTogglingId] = useState<number | null>(null);

    // ── queries ──────────────────────────────────────────────────────────────

    const queryRules = useQuery({
        queryKey: ['nat_port_forward', selectedApplianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: PortForwardRuleDTO[] }>(
                `/api/nat/port-forward?appliance_id=${selectedApplianceId}`,
                { withCredentials: true }
            );
            return res.data.result ?? [];
        },
        enabled: !!selectedApplianceId,
    });

    const queryDevices = useQuery({
        queryKey: ['appliance_devices', selectedApplianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: NetworkLanDeviceDTO[] }>(
                `/api/lan_dispositivo?fi=${encodeURIComponent(JSON.stringify({ appliance_id: selectedApplianceId }))}`,
                { withCredentials: true }
            );
            return res.data.result ?? [];
        },
        enabled: !!selectedApplianceId,
    });

    // Links WAN para o link de entrada (mesma query/cache da tela de redirecionamento de tráfego).
    const queryAppliance = useQuery({
        queryKey: ['appliance', selectedApplianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: ApplianceDTO }>(`/api/appliance/${selectedApplianceId}`);
            return res.data.result;
        },
        enabled: !!selectedApplianceId,
    });
    const wans = queryAppliance.data?.redesWan ?? [];
    const nomeWan = (id: number) => wans.find((w) => w.id === id)?.nome ?? `#${id}`;
    const wanOptions = [
        { id: '', label: 'Todos os links' },
        ...wans.map((w) => ({ id: String(w.id), label: w.nome })),
    ];
    // Só o adapter MikroTik prende o dst-nat a um link (in-interface).
    const linkSuportado = (queryAppliance.data?.agent_type ?? selectedAppliance?.agent_type) !== 'opnsense';

    // Quem acessa cada redirecionamento vem das políticas de firewall de entrada.
    const queryPoliticas = useQuery({
        queryKey: ['fw_inbound_policies', selectedApplianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: InboundPolicyDTO[] }>(
                `/api/firewall/inbound-policies?applianceId=${selectedApplianceId}`,
                { withCredentials: true }
            );
            return res.data.result ?? [];
        },
        enabled: !!selectedApplianceId,
    });

    /** Políticas ativas que cobrem o redirecionamento, na ordem em que são avaliadas. */
    const politicasDe = (ruleId: number) =>
        (queryPoliticas.data ?? []).filter(
            (p) =>
                p.enabled &&
                (p.scope !== 'servicos' || p.services.some((s) => s.tipo === 'redirecionamento' && s.id === ruleId))
        );

    const deviceOptions = [
        { id: '', label: '— Digitar IP manualmente —' },
        ...(queryDevices.data ?? []).map((d) => ({
            id: String(d.id),
            label: `${d.nome} (${d.ip})`,
        })),
    ];

    // ── mutations ─────────────────────────────────────────────────────────────

    const mutCreate = useMutation({
        mutationFn: async (body: object) =>
            axios.post('/api/nat/port-forward', body, { withCredentials: true }),
        onSuccess: () => {
            qc.invalidateQueries(['nat_port_forward', selectedApplianceId]);
            toastSuccess('Regra criada com sucesso!');
            handleCloseModal();
        },
        onError: (err: unknown) => {
            const { message } = extractApiError(err, 'Erro ao criar regra');
            toastError(message);
        },
    });

    const mutUpdate = useMutation({
        mutationFn: async ({ id, body }: { id: number; body: object }) =>
            axios.put(`/api/nat/port-forward/${id}`, body, { withCredentials: true }),
        onSuccess: () => {
            qc.invalidateQueries(['nat_port_forward', selectedApplianceId]);
            toastSuccess('Regra atualizada com sucesso!');
            handleCloseModal();
        },
        onError: (err: unknown) => {
            const { message } = extractApiError(err, 'Erro ao atualizar regra');
            toastError(message);
        },
    });

    const mutDelete = useMutation({
        mutationFn: async (id: number) =>
            axios.delete(`/api/nat/port-forward/${id}`, { withCredentials: true }),
        onSuccess: () => {
            qc.invalidateQueries(['nat_port_forward', selectedApplianceId]);
            toastSuccess('Regra removida com sucesso!');
        },
        onError: (err: unknown) => {
            const { message } = extractApiError(err, 'Erro ao remover regra');
            toastError(message);
        },
    });

    const handleToggle = useCallback(async (rule: PortForwardRuleDTO) => {
        if (togglingId) return;
        setTogglingId(rule.id);
        try {
            await axios.patch(`/api/nat/port-forward/${rule.id}/toggle`, {}, { withCredentials: true });
            qc.invalidateQueries(['nat_port_forward', selectedApplianceId]);
        } catch (err: unknown) {
            const { message } = extractApiError(err, 'Erro ao alterar estado da regra');
            toastError(message);
        } finally {
            setTogglingId(null);
        }
    }, [togglingId, selectedApplianceId, qc]);

    // ── modal ─────────────────────────────────────────────────────────────────

    const handleOpenCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormError(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (rule: PortForwardRuleDTO) => {
        setEditing(rule);
        setForm({
            name: rule.name,
            enabled: rule.enabled,
            protocol: rule.protocol,
            externalPort: rule.externalPort,
            internalDeviceId: rule.internalDeviceId != null ? String(rule.internalDeviceId) : '',
            internalIp: rule.internalIp,
            internalPort: rule.internalPort ?? '',
            wanId: rule.wanId != null ? String(rule.wanId) : '',
            comment: rule.comment ?? '',
        });
        setFormError(null);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditing(null);
        setFormError(null);
    };

    const handleDeviceSelect = (deviceId: string | null) => {
        const device = (queryDevices.data ?? []).find((d) => String(d.id) === deviceId);
        setForm((f) => ({
            ...f,
            internalDeviceId: deviceId ?? '',
            internalIp: device ? device.ip : f.internalIp,
        }));
    };

    const handleSubmit = () => {
        const error = validateForm(form);
        if (error) {
            setFormError(error);
            return;
        }
        setFormError(null);

        const deviceId = form.internalDeviceId !== '' ? Number(form.internalDeviceId) : null;
        const body = {
            appliance_id: Number(selectedApplianceId),
            name: form.name.trim(),
            enabled: form.enabled,
            protocol: form.protocol,
            externalPort: form.externalPort.trim(),
            internalIp: form.internalIp.trim(),
            internalPort: form.internalPort.trim() || null,
            internalDeviceId: deviceId,
            wanId: form.wanId !== '' ? Number(form.wanId) : null,
            comment: form.comment.trim() || null,
        };

        if (editing) {
            mutUpdate.mutate({ id: editing.id, body });
        } else {
            mutCreate.mutate(body);
        }
    };

    // ── tabela ────────────────────────────────────────────────────────────────

    const rules = queryRules.data ?? [];

    const headers: DataTableHeader[] = [
        { label: 'Nome', column: 'name', width: '22%' },
        {
            label: 'Protocolo',
            width: '90px',
            alignCenter: true,
            render: (row: PortForwardRuleDTO) => (
                <Badge variant="info">{PROTOCOL_LABEL[row.protocol]}</Badge>
            ),
        },
        { label: 'Porta Externa', width: '120px', alignCenter: true, render: (row: PortForwardRuleDTO) => row.externalPort },
        {
            label: 'Link de entrada',
            width: '140px',
            render: (row: PortForwardRuleDTO) =>
                row.wanId != null ? <Badge variant="info">{nomeWan(row.wanId)}</Badge> : 'Todos',
        },
        {
            label: 'Destino Interno',
            width: '22%',
            render: (row: PortForwardRuleDTO) => `${row.internalIp}:${row.internalPort}`,
        },
        {
            label: 'Acesso',
            width: '110px',
            alignCenter: true,
            render: (row: PortForwardRuleDTO) => {
                const politicas = politicasDe(row.id);
                return politicas.length === 0 ? (
                    <span title="Nenhuma política de entrada cobre este redirecionamento">
                        <Badge variant="warning">Aberto</Badge>
                    </span>
                ) : (
                    <span title={politicas.map((p) => p.name).join(', ')}>
                        <Badge variant="info">
                            {politicas.length === 1 ? '1 política' : `${politicas.length} políticas`}
                        </Badge>
                    </span>
                );
            },
        },
        {
            label: 'Ativo',
            width: '70px',
            alignCenter: true,
            render: (row: PortForwardRuleDTO) => (
                <Botao
                    size="sm"
                    variant="none-primary"
                    icon={row.enabled ? mdiToggleSwitch : mdiToggleSwitchOffOutline}
                    iconProps={{ color: row.enabled ? 'var(--color-success)' : 'var(--color-muted)' }}
                    disabled={togglingId === row.id}
                    onClick={() => handleToggle(row)}
                />
            ),
        },
        {
            label: 'Ações',
            width: '110px',
            stickRight: true,
            alignRight: true,
            render: (row: PortForwardRuleDTO) => (
                <HBox style={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <Botao
                        size="sm"
                        variant="outline-primary"
                        icon={mdiPencil}
                        onClick={() => handleOpenEdit(row)}
                        title="Editar regra"
                    />
                    <BotaoPerigoso
                        size="sm"
                        variant="outline-danger"
                        icon={mdiTrashCan}
                        onConfirm={() => mutDelete.mutate(row.id)}
                    />
                </HBox>
            ),
        },
    ];

    const isSaving = mutCreate.isLoading || mutUpdate.isLoading;

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <LayoutUsuario>
            <VBox style={{ padding: '24px', gap: '16px', height: '100%', overflow: 'hidden' }}>
                {/* Cabeçalho */}
                <HBox style={{ alignItems: 'flex-start' }}>
                    <VBox style={{ flex: 1 }}>
                        <Titulo>Redirecionamento de Portas{selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}</Titulo>
                        <Descricao>Encaminhe portas externas (Internet) para dispositivos internos da rede</Descricao>
                    </VBox>
                    <Botao
                        variant="primary"
                        icon={mdiPlus}
                        onClick={handleOpenCreate}
                        disabled={!selectedApplianceId}
                    >
                        Nova Regra
                    </Botao>
                </HBox>

                {/* Mensagem de seleção */}
                {!selectedApplianceId && (
                    <VBox style={{ alignItems: 'center', padding: '48px', color: 'var(--color-muted)', gap: '8px' }}>
                        <Icon path={mdiSwapHorizontal} size={2} />
                        <span>Selecione uma appliance no menu lateral para gerenciar as regras de redirecionamento</span>
                    </VBox>
                )}

                {/* Loading */}
                {selectedApplianceId && queryRules.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                        <Icon path={mdiLoading} spin size={2} />
                    </HBox>
                )}

                {/* Erro */}
                {selectedApplianceId && queryRules.isError && (
                    <p style={{ color: 'var(--color-danger)' }}>Erro ao carregar regras. Tente novamente.</p>
                )}

                {/* Tabela */}
                {selectedApplianceId && !queryRules.isLoading && !queryRules.isError && (
                    <DataTable
                        cabecalhos={headers}
                        linhas={rules}
                    />
                )}
            </VBox>

            {/* ── Modal criar / editar ─────────────────────────────────────── */}
            <Modal
                show={modalOpen}
                title={editing ? 'Editar Regra de Redirecionamento' : 'Nova Regra de Redirecionamento'}
                onClose={handleCloseModal}
                onCancel={handleCloseModal}
                onConfirm={handleSubmit}
                confirmProps={{ label: editing ? 'Salvar' : 'Criar', disabled: isSaving }}
                cancelProps={{ label: 'Cancelar', disabled: isSaving }}
                size="md"
            >
                <VBox style={{ gap: '14px' }}>
                    {/* Nome */}
                    <CampoTexto
                        label="Nome da regra"
                        value={form.name}
                        onChange={(v: string) => setForm((f) => ({ ...f, name: v }))}
                        placeholder="Ex: HTTP Servidor Web"
                        required
                    />

                    {/* Protocolo */}
                    <CampoSeletor
                        label="Protocolo"
                        value={form.protocol || null}
                        onChange={(v) => setForm((f) => ({ ...f, protocol: (v ?? 'tcp') as PortForwardProtocol }))}
                        options={PROTOCOL_OPTIONS}
                        required
                    />

                    {/* Porta externa */}
                    <CampoTexto
                        label="Porta externa (Internet)"
                        value={form.externalPort}
                        onChange={(v: string) => setForm((f) => ({ ...f, externalPort: v }))}
                        placeholder="Ex: 80 ou 8080-8090"
                        required
                        error={form.externalPort && !isValidExternalPort(form.externalPort) ? "Porta inválida" : undefined}
                    />

                    {/* Link de entrada */}
                    {((linkSuportado && wans.length > 1) || form.wanId !== '') && (
                        <CampoSeletor
                            label="Link de entrada (opcional)"
                            value={form.wanId || null}
                            onChange={(v) => setForm((f) => ({ ...f, wanId: v ?? '' }))}
                            options={wanOptions}
                            nullable
                            noSearch
                            hint="Publica a porta só no link escolhido. Presas a links diferentes, duas regras podem usar a mesma porta externa."
                            error={!linkSuportado && form.wanId !== '' ? 'Este agente não suporta link de entrada — use "Todos os links".' : undefined}
                        />
                    )}

                    {/* Dispositivo interno */}
                    <CampoSeletor
                        label="Dispositivo interno (opcional)"
                        value={form.internalDeviceId || null}
                        onChange={handleDeviceSelect}
                        options={deviceOptions}
                        nullable
                        noSearch={deviceOptions.length <= 6}
                    />

                    {/* IP + Porta internos */}
                    <HBox style={{ gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                            <CampoTexto
                                label="IP interno"
                                value={form.internalIp}
                                onChange={(v: string) => setForm((f) => ({ ...f, internalIp: v }))}
                                placeholder="Ex: 192.168.1.10"
                                required
                                error={form.internalIp && !isValidIPv4(form.internalIp) ? "IPv4 inválido" : undefined}
                            />
                        </div>
                        <div style={{ width: '130px' }}>
                            <CampoTexto
                                label="Porta interna"
                                value={form.internalPort}
                                onChange={(v: string) => setForm((f) => ({ ...f, internalPort: v }))}
                                placeholder="1-65535"
                                error={
                                    form.internalPort && !isValidInternalPort(form.internalPort, form.externalPort)
                                        ? (portRangeCount(form.externalPort) > 1
                                            ? `${portRangeCount(form.externalPort)} portas`
                                            : '1-65535')
                                        : undefined
                                }
                            />
                        </div>
                    </HBox>

                    {/* Acesso (somente leitura) */}
                    <VBox style={{ gap: '4px' }}>
                        <label style={{ fontWeight: 500, fontSize: '14px' }}>Acesso</label>
                        <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                            {editing && politicasDe(editing.id).length > 0
                                ? `Controlado por: ${politicasDe(editing.id).map((p) => p.name).join(', ')} (Firewall › Entrada).`
                                : 'Aberto a qualquer origem fora da LAN. Para restringir quem acessa, crie uma política em Firewall › Entrada.'}
                        </p>
                    </VBox>

                    {/* Comentário */}
                    <CampoTexto
                        label="Comentário (opcional)"
                        value={form.comment}
                        onChange={(v: string) => setForm((f) => ({ ...f, comment: v }))}
                        placeholder="Descrição adicional..."
                    />

                    {/* Ativo */}
                    <HBox style={{ gap: '8px', alignItems: 'center' }}>
                        <input
                            type="checkbox"
                            id="nat-enabled"
                            checked={form.enabled}
                            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                        />
                        <label htmlFor="nat-enabled" style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Regra ativa
                        </label>
                    </HBox>

                    {/* Erro de validação */}
                    {formError && (
                        <p style={{ color: 'var(--color-danger)', fontSize: '13px', margin: 0 }}>
                            {formError}
                        </p>
                    )}
                </VBox>
            </Modal>
        </LayoutUsuario>
    );
}
